import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionType, ViewType, Colors, DataType } from '../../config/Constants';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button, ButtonGroup, Toolbar, ToolbarSpacer } from '@progress/kendo-react-buttons';
import { EntityStatusType, EnumType } from '../../config/Enums';
import { Container, Row, Col } from 'react-bootstrap';
import { Breadcrumb, BreadcrumbLinkKeyDownEvent, BreadcrumbLinkMouseEvent, DataModel } from '@progress/kendo-react-layout';
import { GenericLoadingPanel } from './panels/Panels';
import { OnDemandIcon } from '../helpers/IconLoader';
import { CommonResources } from '../../config/CommonResources';
import { IQueryProps } from '../services/GraphQLShared';
import { FieldBox, FieldDataBox, FieldEnumBox } from './boxes/FieldBox';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Form, FormRenderProps } from '@progress/kendo-react-form';
import { SvgIcon } from '@progress/kendo-react-common';
import { divideIcon } from '@progress/kendo-svg-icons';

export interface IFilterProps {
  type: DataType;
  enum?: EnumType;
  dataSource?: IQueryProps;
  name: string;
  placeholder?: string;
  required?: boolean;
  title?: string;
  data?: any;
  defaultValue?: any;
  onChangeEvent: (e: any) => void;
}

export interface IToolbarProps {
  DisplayName?: string;
  Title: string;
  Type: ActionType;
  Visible: boolean;
  Icon?: string;
  handleClick: React.MouseEventHandler<HTMLButtonElement>;
}

export interface ILinkProps {
  Title: string;
  Path?: string;
  Active: boolean;
  Icon?: string;
}

export interface IBreadcrumbProps {
  data: {
    Label?: string;
    Type: ViewType;
    Status?: string;
    Toolbar: IToolbarProps[];
    Filters: IFilterProps[];
    Links: ILinkProps[];
  };
  loading?: boolean;
}
export function BuildDefaultBreadcrumbBar(
  links: ILinkProps[],
  onClickBack: () => void,
  tools: IToolbarProps[] = [],
  filters: IFilterProps[] = [],
): IBreadcrumbProps {
  return {
    data: {
      Type: ViewType.View,
      Toolbar: [
        ...tools,
        {
          Type: ActionType.Cancel,
          Title: CommonResources.Button_Back,
          handleClick: onClickBack,
          Visible: true,
        },
      ],
      Links: links,
      Filters: filters,
    },
  };
}
const BreadcrumbBar = (props: IBreadcrumbProps): React.ReactElement | null => {
  const navigate = useNavigate();
  const BreadcrumbFilter = (props: { filter: IFilterProps; formRenderProps: FormRenderProps }) => {
    if (props.filter.enum) {
      return (
        <FieldEnumBox
          field={props.filter.name}
          type={DataType.DropDown}
          data={props.filter.data}
          title={props.filter.title}
          required={props.filter.required == true}
          placeholder={props.filter.placeholder}
          onChange={(event: DropDownListChangeEvent) => {
            props.formRenderProps.onChange(props.filter.name, { value: event.value });
            props.filter.onChangeEvent(event.value);
          }}
        />
      );
    }
    if (props.filter.dataSource) {
      return (
        <FieldDataBox
          field={props.filter.name}
          type={DataType.ComboBox}
          dataSource={props.filter.dataSource}
          title={props.filter.title}
          required={props.filter.required == true}
          placeholder={props.filter.placeholder}
          onChange={(event: DropDownListChangeEvent) => {
            props.formRenderProps.onChange(props.filter.name, { value: event.value });
            props.filter.onChangeEvent(event.value);
          }}
        />
      );
    }
    return (
      <FieldBox
        field={props.filter.name}
        title={props.filter.title}
        type={props.filter.type}
        required={props.filter.required == true}
        placeholder={props.filter.placeholder}
        onChange={(event: any) => {
          props.formRenderProps.onChange(props.filter.name, { value: event.value });
          props.filter.onChangeEvent(event.value);
        }}
      />
    );
  };

  const BreadcrumbFilters = () => {
    if (!props.data.Filters || props.data.Filters.length == 0) return null;
    const initialValues = {};
    props.data.Filters.forEach((e) => (initialValues[e.name] = e.defaultValue));
    return (
      <Form
        initialValues={initialValues}
        render={(formRenderProps) => (
          <>
            {props.data.Filters.map((e, index) => (
              <BreadcrumbFilter filter={e} key={index} formRenderProps={formRenderProps} />
            ))}
          </>
        )}
      />
    );
  };
  const getIcon = React.useCallback((e: IToolbarProps): IconProp => {
    switch (e.Type) {
      case ActionType.New:
        return 'plus';
      case ActionType.Cancel:
        return 'arrow-left';
      case ActionType.Refresh:
        return 'redo';
      case ActionType.Edit:
      case ActionType.Detail:
        return ['far', 'pen-to-square'];
      case ActionType.Delete:
        return ['far', 'trash-alt'];
      case ActionType.ExportExcel:
        return ['far', 'file-excel'];
      case ActionType.ExportPDF:
        return ['far', 'file-pdf'];
      case ActionType.Duplicate:
        return ['far', 'copy'];
      case ActionType.Connect:
        return 'plug';
      case ActionType.Sync:
        return 'rotate';
      case ActionType.Calculate:
        return 'calculator';
      case ActionType.Toggle:
        return 'border-all';
      case ActionType.Print:
        return 'print';
      case ActionType.Filter:
        return 'filter';
      case ActionType.Link:
        return 'link';
      case ActionType.Sort:
        return 'sort-amount-down';
      case ActionType.Undelete:
        return ['far', 'play-circle'];
      case ActionType.Disable:
        return ['far', 'pause-circle'];
      case ActionType.ClearFilters:
        return 'filter-circle-xmark';
      case ActionType.Bookmark:
        return ['far', 'bookmark'];
      case ActionType.FullScreen:
        return 'expand';
      case ActionType.OpenReport:
        return 'ranking-star';
      default:
        return ['far', 'square'];
    }
  }, []);

  const StatusFlag = (props: { title: string; color: string }) => {
    return (
      <Button className="k-icon-button" fillMode={'flat'} title={props.title}>
        <OnDemandIcon icon="circle" color={props.color} fixedWidth />
      </Button>
    );
  };
  const BuildToolbar = () => {
    return props.data.Toolbar?.map((e, index) => {
      if (e.Type == ActionType.ShowStatus) {
        if (props.data.Status == EntityStatusType.Active)
          return <StatusFlag key={index} title={EntityStatusType.Active} color={Colors.SuccessColor} />;
        else if (props.data.Status == EntityStatusType.Disable)
          return <StatusFlag key={index} title={EntityStatusType.Disable} color={Colors.WarningColor} />;
        else return <StatusFlag key={index} title={EntityStatusType.Inactive} color={Colors.AlertColor} />;
      } else if (e.Visible)
        return (
          <Button key={index} className="k-icon-button" fillMode={'flat'} title={e.Title} onClick={e.handleClick}>
            <OnDemandIcon icon={getIcon(e)} fixedWidth />
          </Button>
        );
      return null;
    });
  };

  const BreadcrumbToolbar = () => {
    const totalItems = !props.data.Toolbar ? 0 : props.data.Toolbar.length;
    if (totalItems > 1) return <ButtonGroup>{BuildToolbar()}</ButtonGroup>;
    return <>{BuildToolbar()}</>;
  };

  const dataLink = React.useCallback(() => {
    return props.data.Links.map((e, index) =>
      e.Icon ? { id: index.toString(), text: e.Title, icon: e.Icon } : { id: index.toString(), text: e.Title },
    );
  }, [props.data.Links]);

  const handleItemSelect = (event: BreadcrumbLinkMouseEvent) => {
    const element = dataLink().find((e: DataModel) => e.id == event?.id);
    if (element) {
      const elementLink = props.data.Links.find((e) => e.Title == element.text);
      if (elementLink && elementLink.Path) {
        navigate(elementLink.Path);
      }
    }
  };
  const handleKeyDown = (event: BreadcrumbLinkKeyDownEvent) => {
    if (event.nativeEvent.keyCode === 13) {
      const element = dataLink().find((e: DataModel) => e.id == event?.id);
      if (element) {
        const elementLink = props.data.Links.find((e) => e.Title == element.text);
        if (elementLink && elementLink.Path) {
          navigate(elementLink.Path);
        }
      }
    }
  };
  return (
    <Container fluid className="breadcrumb-bar px-4" style={{ position: 'sticky' }}>
      <Row>
        <Col>
          <Toolbar size='small'>
            <Breadcrumb data={dataLink()} onItemSelect={handleItemSelect} onKeyDown={handleKeyDown} breadcrumbDelimiter={() => <SvgIcon icon={divideIcon} />}/>
            <ToolbarSpacer />
            <GenericLoadingPanel loading={props.loading === true} size="md" />
            <BreadcrumbFilters />
            <BreadcrumbToolbar />
          </Toolbar>
        </Col>
      </Row>
    </Container>
  );
};

export default BreadcrumbBar;
